import { formatDateAsDate } from "./formatters";

function normalizeFilialId(v) {
    if (v == null || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
}

function filialIdsEqual(a, b) {
    return normalizeFilialId(a) === normalizeFilialId(b);
}

function normalizeAccreditationName(s) {
    return String(s ?? "")
        .trim()
        .toLowerCase()
        .replace(/\s+/g, " ");
}

const ACCREDITATION_LAYOUT_PAIRS = [
    ["A", "А"], ["B", "В"], ["C", "С"], ["E", "Е"],
    ["H", "Н"], ["K", "К"], ["M", "М"], ["O", "О"],
    ["P", "Р"], ["T", "Т"], ["X", "Х"], ["Y", "У"],
    ["Q", "Й"], ["W", "Ц"], ["R", "К"], ["U", "Г"],
    ["I", "Ш"], ["F", "А"], ["G", "П"], ["J", "О"],
    ["L", "Д"], ["Z", "Я"], ["V", "М"], ["N", "Т"],
];

function getAccreditationCodeVariants(code) {
    const normalized = String(code || "").toUpperCase();
    const match = normalized.match(/^(KZ\.[A-ZА-ЯЁ]+\.\d{1,4}\.)([A-ZА-ЯЁ]?\d{1,4})$/i);
    if (!match) return new Set([normalized]);

    const layout = new Map();
    for (const [latin, cyrillic] of ACCREDITATION_LAYOUT_PAIRS) {
        if (!layout.has(latin)) layout.set(latin, new Set());
        if (!layout.has(cyrillic)) layout.set(cyrillic, new Set());
        layout.get(latin).add(cyrillic);
        layout.get(cyrillic).add(latin);
    }

    let variants = [""];
    for (const char of match[2]) {
        const alternatives = [char, ...(layout.get(char) || [])];
        variants = variants.flatMap((prefix) =>
            alternatives.map((alternative) => prefix + alternative)
        );
    }

    const result = new Set(variants.map((suffix) => match[1] + suffix));
    result.add(normalized);
    return result;
}

export function accreditationCodesMatch(left, right) {
    const leftCode = extractAccreditationCode(left);
    const rightCode = extractAccreditationCode(right);
    if (!leftCode || !rightCode) return false;

    const rightVariants = getAccreditationCodeVariants(rightCode);
    return [...getAccreditationCodeVariants(leftCode)]
        .some((variant) => rightVariants.has(variant));
}

function extractAccreditationCode(value) {
    return String(value || "").match(
        /KZ\.[A-ZА-ЯЁ]+\.\d{1,4}\.(?:\d{1,4}|[A-ZА-ЯЁ]\d{1,4})/i
    )?.[0]?.toUpperCase() || "";
}

/**
 * Находит запись accreditationIndicator по тем же filialId и filialDivisionId, что у выбранной accreditations.
 * При нескольких записях: не isIrrelevant, затем с наиболее поздним validityPeriod.
 */
export function findAccreditationIndicatorByFilial(indicators, accreditation) {
    if (!indicators?.length || !accreditation) return null;
    const aF = accreditation.filialId ?? accreditation.FilialId;
    const aD = accreditation.filialDivisionId ?? accreditation.FilialDivisionId;

    const matches = indicators.filter(
        (x) => filialIdsEqual(aF, x?.filialId) && filialIdsEqual(aD, x?.filialDivisionId)
    );
    if (!matches.length) return null;

    const active = matches.filter((x) => !x?.isIrrelevant);
    const pool = active.length ? active : matches;
    return [...pool].sort((a, b) => {
        const ta = new Date(a?.validityPeriod || 0).getTime();
        const tb = new Date(b?.validityPeriod || 0).getTime();
        return tb - ta;
    })[0];
}

/**
 * Сопоставление по названию (AccreditationsDto.Name) с учетом номера аттестата в имени.
 * Индикатор принимается только при совпадении названия и filialId ИЛИ filialDivisionId.
 */
export function findAccreditationIndicatorByName(indicators, accreditationName, accreditation = null) {
    if (!indicators?.length || accreditationName == null || accreditationName === "") return null;
    const key = normalizeAccreditationName(accreditationName);

    // Название само по себе не является достаточным ключом: одинаковые знаки
    // могут быть заведены для разных филиалов/районных отделений. При наличии
    // записи знака оставляем только индикаторы того же филиала ИЛИ отдела.
    const accreditationFilialId = accreditation?.filialId ?? accreditation?.FilialId;
    const accreditationDivisionId = accreditation?.filialDivisionId ?? accreditation?.FilialDivisionId;
    const hasScope = accreditationFilialId != null || accreditationDivisionId != null;
    const scoped = hasScope
        ? indicators.filter((x) =>
            (accreditationFilialId != null && filialIdsEqual(accreditationFilialId, x?.filialId ?? x?.FilialId)) ||
            (accreditationDivisionId != null && filialIdsEqual(accreditationDivisionId, x?.filialDivisionId ?? x?.FilialDivisionId))
        )
        : [];

    // Если у знака нет филиала/отдела, не подставляем потенциально чужие данные.
    if (!scoped.length) return null;

    const chooseCurrent = (rows) => {
        if (!rows.length) return null;
        const active = rows.filter((x) => !x?.isIrrelevant);
        const pool = active.length ? active : rows;
        return [...pool].sort((a, b) => {
            const ta = new Date(a?.validityPeriod || 0).getTime();
            const tb = new Date(b?.validityPeriod || 0).getTime();
            return tb - ta;
        })[0];
    };

    const requestedCode = extractAccreditationCode(key);
    const exact = scoped.filter((x) => {
        const candidateName = normalizeAccreditationName(x?.name);
        if (candidateName === key) return true;
        const candidateCode = extractAccreditationCode(candidateName);
        return requestedCode && candidateCode && accreditationCodesMatch(requestedCode, candidateCode);
    });
    const byExact = chooseCurrent(exact);
    if (byExact) return byExact;

    // имя на логотипе часто содержит номер аттестата, например "KZ.T.11.2911 TESTING"
    const byCertificate = [];
    for (const x of scoped) {
        const cert = String(x?.certificateNumber ?? "").trim();
        if (!cert) continue;
        const ncert = normalizeAccreditationName(cert);
        if (ncert && key.includes(ncert)) byCertificate.push(x);
    }
    const certificateMatch = chooseCurrent(byCertificate);
    if (certificateMatch) return certificateMatch;

    // частичное совпадение названия
    const partial = [];
    for (const x of scoped) {
        const xn = normalizeAccreditationName(x?.name);
        if (!xn) continue;
        if (key.includes(xn) || xn.includes(key)) partial.push(x);
    }

    return chooseCurrent(partial);
}

/** Атауы (күн + ай + жыл) — Intl/kk-KZ нередко откатывается на русский, поэтому явный список */
const KAZAKH_MONTH_NAMES = [
    "қаңтар",
    "ақпан",
    "наурыз",
    "сәуір",
    "мамыр",
    "маусым",
    "шілде",
    "тамыз",
    "қыркүйек",
    "қазан",
    "қараша",
    "желтоқсан",
];

export function formatProtocolAccreditationDateRu(isoOrDate) {
    if (!isoOrDate) return "";
    const d = new Date(isoOrDate);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("ru-RU", {
        day: "numeric",
        month: "long",
        year: "numeric",
    });
}

/**
 * Дата для блока KZ в протоколе (текст «как в документе»), без зависимости от браузерной локали kk.
 * Формат: «3 шілде 2025 ж.» (аналог русского «3 июля 2025 г.»).
 */
export function formatProtocolAccreditationDateKz(isoOrDate) {
    if (!isoOrDate) return "";
    const d = new Date(isoOrDate);
    if (Number.isNaN(d.getTime())) return "";
    const day = d.getDate();
    const month = d.getMonth();
    const year = d.getFullYear();
    const monthName = KAZAKH_MONTH_NAMES[month];
    if (!monthName) return formatDateAsDate(isoOrDate);
    return `${day} ${monthName} ${year} ж.`;
}

/**
 * Извлекает data URL/base64 из Sign (camelCase / PascalCase, строка или объект).
 */
export function getSignBase64(sign) {
    if (sign == null) return "";
    if (typeof sign === "string") {
        const t = sign.trim();
        if (t.startsWith("data:") || t.startsWith("http://") || t.startsWith("https://")) {
            return t;
        }
        try {
            return getSignBase64(JSON.parse(sign));
        } catch {
            return "";
        }
    }
    if (typeof sign === "object") {
        return sign.base64 || sign.Base64 || "";
    }
    return "";
}

/**
 * Поля protocol.data.accreditationInfo из записи реестра показателей аккредитации.
 */
export function buildAccreditationInfoFromIndicator(indicator) {
    if (!indicator) return null;
    return {
        markNumber: indicator.certificateNumber || indicator.name || "",
        markName: indicator.name || "",
        registeredAtKz: formatProtocolAccreditationDateKz(indicator.issueDate),
        registeredAtRu: formatProtocolAccreditationDateRu(indicator.issueDate),
        validUntilKz: formatProtocolAccreditationDateKz(indicator.validityPeriod),
        validUntilRu: formatProtocolAccreditationDateRu(indicator.validityPeriod),
        registeredAt: formatDateAsDate(indicator.issueDate),
        validUntil: formatDateAsDate(indicator.validityPeriod),
    };
}
